import { useEffect, useState } from 'react';

import store from 'js/lib/coursera.store';
import { get as getUser } from 'js/lib/user';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import type { EnrollmentChoices } from 'bundles/enroll/components/xdp/withEnrollment';
import googleCertificateExperiments from 'bundles/epic/clients/GoogleCertificate';
import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import type CourseTypeMetadataV1 from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import { CourseType } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import type OpenCourseMembershipsV1 from 'bundles/naptimejs/resources/openCourseMemberships.v1';

import _t from 'i18n!nls/enroll';

export const generateImpressionForFreemiumModelExperiment = ({ courseId }: { courseId?: string }) => {
  // Generate impression if passing in a courseId from XDP enroll modal
  if (!courseId) {
    return;
  }

  // eslint-disable-next-line camelcase
  googleCertificateExperiments.get('freemiumModelAaTest', { course_id: courseId });

  // eslint-disable-next-line camelcase
  googleCertificateExperiments.get('isFreemiumModelEnabled', { course_id: courseId });
};

export const isFreemiumModelEnabled = (courseId?: string) => {
  if (!courseId) {
    return false;
  }

  // eslint-disable-next-line camelcase
  return googleCertificateExperiments.preview('isFreemiumModelEnabled', { course_id: courseId });
};

// The checkout confirmation flow changes for Freemium should apply to all checkouts with a courseId attached,
// even for courses not directly part of the Freemium experience
export const isFreemiumCheckoutConfirmationFlowEnabled = () => {
  // Pass in a hardcoded courseId so that we skip the course exclusion list and return true
  // as long as the learner is in the enabled variant
  // eslint-disable-next-line camelcase
  return googleCertificateExperiments.preview('isFreemiumModelEnabled', { course_id: 'always-enable' });
};

export const isEnrolledThroughFreemiumAccess = (membership?: OpenCourseMembershipsV1) => {
  if (!membership) {
    return false;
  }

  return isFreemiumModelEnabled(membership.courseId) && membership.isFreemiumLearner;
};

// TODO update description copies once they are finalized
export const getFreemiumEnrollModalCopy = () => ({
  auditOnly: _t('Preview this course'),
  auditOnlyDescription: _t('You will have access to all course materials except graded items'),
  fullCourseNoCertificate: _t('Preview this course'),
  fullCourseNoCertificateDescription: _t('You will still have access to all course materials for this course'),
});

export const canEnrollThroughFreemium = ({
  courseId,
  enrollmentAvailableChoices,
  courseTypeMetadataWithVersion,
}: {
  courseId: string;
  enrollmentAvailableChoices: EnrollmentChoices;
  courseTypeMetadataWithVersion?: CourseTypeMetadataV1;
}) => {
  if (!isFreemiumModelEnabled(courseId)) {
    return false;
  }

  const { canSubscribeToS12n, canPurchaseSingleCourse, canAuditCourse, hasFreeEnrollOptionIntoCourse } =
    enrollmentAvailableChoices;

  const canAuditAndSubscribeToS12n = canSubscribeToS12n && canAuditCourse;
  const canAuditAndPurchaseCourse = canPurchaseSingleCourse && canAuditCourse;
  const canFreeEnrollAndPurchaseCourse = canPurchaseSingleCourse && hasFreeEnrollOptionIntoCourse;
  const isStandardCourse = courseTypeMetadataWithVersion?.courseTypeMetadata.typeName === CourseType.STANDARD_COURSE;

  return (
    isStandardCourse && (canAuditAndSubscribeToS12n || canAuditAndPurchaseCourse || canFreeEnrollAndPurchaseCourse)
  );
};

const FIRST_MODULE_COMPLETION_KEY = 'firstModuleCompletions';

/**
 * Sets or updates a value in localStorage to track that a learner has completed the first module in a course.
 * @param courseId - The ID of the course where the first module was completed
 */
export const setHasCompletedFirstModuleToLocalStorage = (courseId: string): void => {
  const userId = getUser()?.id;
  if (!userId || !courseId) {
    return;
  }

  const existingCompletions = localStorageEx.getItem(FIRST_MODULE_COMPLETION_KEY, JSON.parse, {});
  if (!existingCompletions[userId]) {
    existingCompletions[userId] = [courseId];
  } else if (!existingCompletions[userId].includes(courseId)) {
    existingCompletions[userId].push(courseId);
  }
  localStorageEx.setItem(FIRST_MODULE_COMPLETION_KEY, existingCompletions, JSON.stringify);
};

/**
 * Checks if a learner has completed the first module in a course.
 * @param courseId - The ID of the course to check
 * @returns boolean - True if the learner has completed the first module, false otherwise
 */
export const hasCompletedFirstModuleInLocalStorage = (courseId: string): boolean => {
  const userId = getUser()?.id;
  const existingCompletions = localStorageEx.getItem(FIRST_MODULE_COMPLETION_KEY, JSON.parse, {});
  return Boolean(existingCompletions[userId]?.includes(courseId));
};

/**
 * Checks if a learner is eligible to access Coach for a specific course
 * @param courseId - The ID of the course to check
 * @returns {object} - Returns an object with the following properties:
 *   - `isCourseEligibleForCoachAccess`: boolean - True if the learner has can access Coach for the course, false otherwise
 *   - `loading`: boolean - True if the API request is loading, false otherwise
 */
export const useIsCourseEligibleForCoachAccess = (courseId: string) => {
  const [loading, setLoading] = useState(true);
  const [isCourseEligibleForCoachAccess, setIsCourseEligibleForCoachAccess] = useState(false);

  useEffect(() => {
    if (isFreemiumModelEnabled(courseId)) {
      // TODO: replace mocking with actual BE API call
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const promise = new Promise((resolve) => {
        setLoading(true);

        if (typeof window !== 'undefined' && window.location.search.includes('mockCoachAccess=true')) {
          setTimeout(() => {
            setLoading(false);
            setIsCourseEligibleForCoachAccess(true);
            resolve(true);
          }, 1000);
        } else if (typeof window !== 'undefined' && window.location.search.includes('mockCoachAccess=false')) {
          setTimeout(() => {
            setLoading(false);
            resolve(false);
          }, 1000);
        } else {
          setLoading(false);
          resolve(false);
        }
      });
    } else {
      // If freemium model is not enabled, set loading to false immediately
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isCourseEligibleForCoachAccess,
    loading: !isFreemiumModelEnabled(courseId) ? false : loading,
  };
};

export type ConfirmationData = {
  userId: number;
  hasCourseraPlusUpsell: boolean;
  courseId: string;
  s12nId?: string;
};

const CONFIRMATION_DATA_KEY = 'freemiumCheckoutConfirmation';

export const saveCheckoutConfirmationData = ({
  cart,
  hasCourseraPlusUpsell,
}: {
  cart: CartsV2;
  hasCourseraPlusUpsell: boolean;
}) => {
  const s12nId = cart.getSpecializationIdFromCartItem(cart.firstCartItem);
  const courseId = cart.isVC ? cart.productItemId : undefined;

  const data = {
    userId: getUser().id,
    hasCourseraPlusUpsell,
    courseId,
    s12nId,
  };
  store.set(CONFIRMATION_DATA_KEY, JSON.stringify(data));
};

export const clearCheckoutConfirmationData = () => {
  store.remove(CONFIRMATION_DATA_KEY);
};

export const getCheckoutConfirmationData = (): ConfirmationData | undefined => {
  const rawData = store.get(CONFIRMATION_DATA_KEY);
  return rawData ? JSON.parse(rawData) : undefined;
};
